/**
 * WHAT'S NEW, on every home (DECISIONS 120, Bobbie, 2026-10-07: "can we create
 * a whats new card based on role?").
 *
 * Shows the entries in src/lib/whatsNew.ts for the viewer's role that she has
 * not cleared, newest first with the Shout-out Wall entry on top. It is a
 * MONTHLY roll-up (Bobbie, 2026-11-03: "once a month not daily"): only last
 * month's changes, and after Got it the card is gone until next month's. Got it
 * clears every entry the card holds, in one tap, on every device and on every
 * sign-in she has (netlify/functions/whatsNewSeen.mjs). With nothing to show,
 * it renders nothing at all.
 *
 * A first visit asks the server to write her start day once, and the card
 * stays out of the way until that answer is back, so a new account never
 * flashes months of backlog first. If the server cannot be reached the card
 * simply does not show; it is never in the way of the home.
 *
 * Mobile first: every control is at least 44px tall, and the list past three
 * entries folds behind "See what changed", with a search box once it is long.
 */
import { useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../lib/auth-context';
import { useUserDoc } from '../lib/users';
import type { UserRole } from '../lib/userRole';
import {
  WHATS_NEW_COLLAPSED_COUNT,
  WHATS_NEW_SEARCH_THRESHOLD,
  chunkWhatsNewIds,
  searchWhatsNew,
  visibleWhatsNew,
  whatsNewCandidates,
  whatsNewMonthLabel,
  whatsNewRollupMonth,
  type WhatsNewEntry,
} from '../lib/whatsNew';
import { postWhatsNewSeen, useWhatsNewState } from '../lib/whatsNewClient';
import SearchBar from './SearchBar';

const cardStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--space-3)',
  padding: 'var(--space-4)',
  borderRadius: 'var(--radius-lg, 16px)',
  background: 'var(--paper)',
  border: '1px solid var(--line)',
  color: 'var(--ink)',
  textAlign: 'left',
  width: '100%',
  boxSizing: 'border-box',
};

const headStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 'var(--space-2)',
};

const eyebrowStyle: CSSProperties = {
  margin: 0,
  fontSize: 12,
  fontWeight: 800,
  letterSpacing: 0.8,
  textTransform: 'uppercase',
  color: 'var(--ink-soft)',
};

const entryStyle = (featured: boolean): CSSProperties => ({
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--space-1)',
  padding: 'var(--space-3)',
  borderRadius: 'var(--radius-md)',
  background: featured ? 'var(--lemon-wash)' : 'transparent',
  border: `1px solid ${featured ? 'var(--lemon-border)' : 'var(--line)'}`,
});

const titleStyle: CSSProperties = { margin: 0, fontSize: 16, fontWeight: 800, lineHeight: 1.3 };
const bodyStyle: CSSProperties = { margin: 0, fontSize: 15, lineHeight: 1.5 };

/**
 * Room every button and link leaves on its right for the floating controls.
 * The Assistant bubble and the back-to-top arrow sit 14px in from the right
 * edge of a phone and are 48px wide (GlobalChrome, AssistantLauncher); the
 * card's own edge is 32px in (the home's 16px gutter plus the card's 16px
 * padding). 40px more keeps every label left of that column on any phone
 * width, so a long one ("Open the Technique command center") wraps instead of
 * running under them. Measured at 390px on the card's first build
 * (2026-10-09).
 */
const FLOATER_ROOM = 'calc(100% - 40px)';

const linkStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  minHeight: 'var(--tap-target)',
  maxWidth: FLOATER_ROOM,
  fontWeight: 800,
  color: 'var(--ink)',
  textDecoration: 'underline',
  alignSelf: 'flex-start',
};

/**
 * The actions STACK, each button at its own width from the left edge, and
 * never share a row. The Assistant bubble and the back-to-top arrow float
 * over the right side of a phone screen (FLOATER_ROOM above), so a row of two
 * buttons ran "See what changed (8 more)" right under them at 390px and cut
 * off its words. The other home cards keep their actions clear the same way:
 * left aligned at their own width (HeadsUpCard, SocialMediaReminderCard),
 * with the page's FAB_CLEARANCE_H reservation below the last card. The
 * floaters themselves are never hidden or moved for this.
 */
const buttonRowStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: 'var(--space-2)',
};

const buttonStyle = (primary: boolean): CSSProperties => ({
  minHeight: 'var(--tap-target)',
  minWidth: 'var(--tap-target)',
  maxWidth: FLOATER_ROOM,
  padding: '0 var(--space-4)',
  borderRadius: 'var(--radius-md)',
  border: primary ? '1px solid transparent' : '1px solid var(--line)',
  background: primary ? 'var(--btn-primary-bg)' : 'var(--paper)',
  color: primary ? 'var(--primary-on)' : 'var(--ink)',
  fontFamily: 'var(--font-body)',
  fontSize: 15,
  fontWeight: 800,
  cursor: 'pointer',
});

const errorStyle: CSSProperties = { margin: 0, fontSize: 14, color: 'var(--rose-deep)' };

function EntryRow({ entry }: { entry: WhatsNewEntry }) {
  return (
    <li style={entryStyle(Boolean(entry.featured))} data-whats-new-entry={entry.id}>
      <h3 style={titleStyle}>{entry.title}</h3>
      <p style={bodyStyle}>{entry.body}</p>
      {entry.link && (
        <Link to={entry.link.href} style={linkStyle}>{entry.link.label}</Link>
      )}
    </li>
  );
}

/**
 * The card itself, given the state. Exported so a harness can draw it without
 * Firebase.
 */
export function WhatsNewCardView({
  entries,
  onClear,
  clearing = false,
  error = null,
  monthLabel = null,
}: {
  entries: readonly WhatsNewEntry[];
  onClear: () => void;
  /** The month this roll-up covers, like "October". */
  monthLabel?: string | null;
  clearing?: boolean;
  error?: string | null;
}) {
  const [expanded, setExpanded] = useState(false);
  const [query, setQuery] = useState('');
  const shown = useMemo(() => {
    if (!expanded) return entries.slice(0, WHATS_NEW_COLLAPSED_COUNT);
    return searchWhatsNew(entries, query);
  }, [entries, expanded, query]);
  if (entries.length === 0) return null;
  const hidden = entries.length - WHATS_NEW_COLLAPSED_COUNT;
  return (
    <section style={cardStyle} aria-labelledby="whats-new-heading" data-whats-new-card>
      <div style={headStyle}>
        <div>
          <p style={eyebrowStyle}>What's new</p>
          <h2 id="whats-new-heading" style={{ margin: 0, fontSize: 20, fontWeight: 800 }}>
            {monthLabel ? `New in the Hub in ${monthLabel}` : 'New in the Hub for you'}
          </h2>
          <p style={{ ...bodyStyle, fontSize: 14, color: 'var(--ink-soft)' }}>
            Once a month. Tap Got it and this card is gone until next month.
          </p>
        </div>
      </div>
      {expanded && entries.length > WHATS_NEW_SEARCH_THRESHOLD && (
        <SearchBar
          value={query}
          onChange={setQuery}
          placeholder="Search what's new"
          resultCount={shown.length}
          totalCount={entries.length}
        />
      )}
      <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
        {shown.map((entry) => <EntryRow key={entry.id} entry={entry} />)}
      </ul>
      {expanded && shown.length === 0 && (
        <p style={bodyStyle}>Nothing matches that search.</p>
      )}
      {error && <p style={errorStyle} role="alert">{error}</p>}
      <div style={buttonRowStyle}>
        <button type="button" style={buttonStyle(true)} onClick={onClear} disabled={clearing}>
          {clearing ? 'Saving...' : 'Got it'}
        </button>
        {hidden > 0 && (
          <button
            type="button"
            style={buttonStyle(false)}
            aria-expanded={expanded}
            onClick={() => { setExpanded((open) => !open); setQuery(''); }}
          >
            {expanded ? 'Show less' : `See what changed (${hidden} more)`}
          </button>
        )}
      </div>
    </section>
  );
}

/**
 * The live card for the signed-in viewer. `role` is passed in by each home,
 * which already knows it ('ensemble' on a Jr Staff's dancer home).
 */
export default function WhatsNewCard({ role }: { role: UserRole | null | undefined }) {
  const { user } = useAuth();
  // The REAL account's role must be the role this home passed, and no "view
  // as" preview may be on: a Director walking the app as a teacher must never
  // clear entries on her own card that she was never shown as herself.
  const { realRole, previewRole } = useUserDoc();
  const own = !previewRole && realRole === role;
  // A role with no news today asks the server for nothing and draws nothing,
  // so no account gets a start write until there is something to show it.
  const hasNews = whatsNewCandidates(role, new Date()) > 0;
  const uid = own && hasNews ? user?.uid ?? null : null;
  const { loading, missing, state, error: readError } = useWhatsNewState(uid);
  const startAsked = useRef<string | null>(null);
  const [cleared, setCleared] = useState<ReadonlySet<string>>(new Set());
  const [clearing, setClearing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [startFailed, setStartFailed] = useState(false);

  useEffect(() => {
    if (!uid || loading || !missing || startAsked.current === uid) return;
    startAsked.current = uid;
    postWhatsNewSeen({ action: 'start' }).catch(() => setStartFailed(true));
  }, [uid, loading, missing]);

  const entries = useMemo(() => {
    const seenIds = new Set([...state.seenIds, ...cleared]);
    return visibleWhatsNew(role, { startDate: state.startDate, seenIds, clearedOn: state.clearedOn }, new Date());
  }, [role, state, cleared]);
  const now = new Date();
  const monthLabel = whatsNewMonthLabel(whatsNewRollupMonth(now), now);

  if (!uid || loading || missing || startFailed || readError) return null;

  // Got it clears every entry the card holds. The lane takes at most
  // WHATS_NEW_CLEAR_MAX_IDS ids a request, so a longer list goes in pieces,
  // one after another; each piece that lands is cleared at once, so a tap
  // again after a dropped connection sends only what is left.
  const clear = () => {
    const pieces = chunkWhatsNewIds(entries.map((entry) => entry.id));
    setClearing(true);
    setError(null);
    (async () => {
      for (const ids of pieces) {
        await postWhatsNewSeen({ action: 'clear', ids });
        setCleared((prev) => new Set([...prev, ...ids]));
      }
    })()
      .catch(() => setError('That did not save. Check your connection and tap Got it again.'))
      .finally(() => setClearing(false));
  };

  return <WhatsNewCardView entries={entries} onClear={clear} clearing={clearing} error={error} monthLabel={monthLabel} />;
}
